import { Link, useNavigate } from 'react-router';
import { db } from '@/db/db';
import { discardRecovered, getRecoverable, saveRecovered } from '@/ride/manager';
import { resumeRide } from '@/ride/launch';
import { getCourse, listRoutes } from '@/routes/library';
import { DEMO_ROUTE_ID } from '@/routes/demo';
import { useSettings } from '@/store/settings';
import { useAccount } from '@/store/account';
import { resolveDisplayName } from '@/store/settingsModel';
import type { RideRecord } from '@/ride/model';
import { fmtDuration, fmtNumber, useT } from '@/ui/i18n';
import { useAsync } from '@/ui/useAsync';
import { TodayCard } from '@/ui/components/TodayCard';
import { FirstStepsCard } from '@/ui/components/FirstStepsCard';
import { ElevationProfile } from '@/ui/components/ElevationProfile';

/** Monday 00:00 of the current week (local time). */
function weekStart(now = new Date()): number {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dow = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - dow);
  return d.getTime();
}

interface Week {
  rides: number;
  sec: number;
  km: number;
  climb: number;
  tss: number;
}

function weekTotals(rides: RideRecord[]): Week {
  const from = weekStart();
  const w: Week = { rides: 0, sec: 0, km: 0, climb: 0, tss: 0 };
  for (const r of rides) {
    if (r.deleted || r.startedAt < from) continue;
    w.rides++;
    w.sec += r.summary.durationSec;
    w.km += r.summary.distanceM / 1000;
    w.climb += r.summary.elevationGainM;
    w.tss += r.summary.tss;
  }
  return w;
}

function fmtHours(sec: number): string {
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  return `${h}:${String(m).padStart(2, '0')}`;
}

export function HomePage() {
  const t = useT();
  const nav = useNavigate();
  const localName = useSettings((s) => s.settings.displayName);
  const accountName = useAccount((s) => s.me?.name);
  const name = resolveDisplayName(localName, accountName);
  const { data: recoverable, reload } = useAsync(() => getRecoverable(), []);
  const { data: rides } = useAsync(
    async () => (await db.rides.orderBy('startedAt').reverse().limit(60).toArray()).filter((r) => !r.deleted),
    [],
  );
  const { data: featured } = useAsync(async () => {
    const all = await listRoutes();
    return all.find((r) => r.meta.builtin && r.id !== DEMO_ROUTE_ID) ?? all[0] ?? null;
  }, []);
  const week = weekTotals(rides ?? []);
  const longest = featured?.climbs.reduce<(typeof featured.climbs)[number] | null>(
    (best, c) => (!best || c.lengthM > best.lengthM ? c : best),
    null,
  );
  const today = new Date().toLocaleDateString('de-CH', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div className="grid" style={{ gap: 24 }}>
      <div className="title-row">
        <div>
          <h1>{t('home.title', { name: name ? ` ${name}` : '' })}</h1>
          <p className="sub">
            {today} ·{' '}
            {week.rides === 0
              ? t('home.weekNone')
              : t('home.weekSome', { n: week.rides, time: fmtHours(week.sec) })}
          </p>
        </div>
        <div className="row">
          <Link className="btn ghost" to="/workouts">
            {t('home.workout')}
          </Link>
          <Link className="btn ghost" to="/gruppenfahrt">
            {t('home.group')}
          </Link>
          <Link className="btn primary" to="/fahren" data-testid="quick-free">
            {t('home.freeRide')}
          </Link>
        </div>
      </div>

      {recoverable && (
        <div className="notice warn spread" data-testid="recovery-banner">
          <span>
            {t('home.recover')} ({fmtDuration(recoverable.samples.length)},{' '}
            {new Date(recoverable.startedAt).toLocaleString('de-CH')})
          </span>
          <div className="row">
            <button
              className="primary"
              onClick={async () => {
                await resumeRide(recoverable, await getCourse(recoverable.routeId));
                nav('/fahren/live');
              }}
              data-testid="recovery-resume"
            >
              {t('home.recoverResume')}
            </button>
            <button
              onClick={async () => {
                const r = await saveRecovered(recoverable);
                nav(`/fahrten/${r.id}`);
              }}
              data-testid="recovery-save"
            >
              {t('home.recoverSave')}
            </button>
            <button
              className="ghost"
              onClick={async () => {
                await discardRecovered();
                reload();
              }}
            >
              {t('home.recoverDiscard')}
            </button>
          </div>
        </div>
      )}

      <section className="kpis" aria-label={t('home.weekLabel')} data-testid="week-kpis">
        <div>
          <div className="k-label">{t('home.kpiTime')}</div>
          <div className="k-value">
            {fmtHours(week.sec)}
            <small>h</small>
          </div>
          <div className="k-note">{t('home.sinceMonday')}</div>
        </div>
        <div>
          <div className="k-label">{t('home.kpiDistance')}</div>
          <div className="k-value">
            {fmtNumber(week.km, 1)}
            <small>km</small>
          </div>
          <div className="k-note">{t('home.kpiRides', { n: week.rides })}</div>
        </div>
        <div>
          <div className="k-label">{t('home.kpiClimb')}</div>
          <div className="k-value">
            {fmtNumber(week.climb)}
            <small>m</small>
          </div>
          <div className="k-note">{t('home.sinceMonday')}</div>
        </div>
        <div>
          <div className="k-label">{t('home.kpiTss')}</div>
          <div className="k-value">{fmtNumber(week.tss)}</div>
          <div className="k-note">{t('home.sinceMonday')}</div>
        </div>
      </section>

      {rides && <FirstStepsCard hasRides={rides.length > 0} />}

      <div className="home-grid" style={{ alignItems: 'start' }}>
        <div className="span-7">
          <TodayCard />
        </div>
        <section className="card span-5" aria-labelledby="last-rides">
          <div className="panel-head">
            <h2 id="last-rides">{t('home.lastRides')}</h2>
            <Link to="/fahrten">{t('home.viewAll')}</Link>
          </div>
          {!rides || rides.length === 0 ? (
            <p className="empty-line">{t('home.noRides')}</p>
          ) : (
            <div className="row-cards">
              {rides.slice(0, 4).map((r) => (
                <Link key={r.id} className="row-card" to={`/fahrten/${r.id}`}>
                  <div style={{ minWidth: 0 }}>
                    <div className="t">{r.name}</div>
                    <div className="s">
                      {new Date(r.startedAt).toLocaleDateString('de-CH')} ·{' '}
                      {fmtDuration(r.summary.durationSec)}
                    </div>
                  </div>
                  <div className="v">
                    <div>{fmtNumber(r.summary.distanceM / 1000, 1)} km</div>
                    <div className="s">{fmtNumber(r.summary.tss)} TSS</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {featured && (
          <section
            className="card span-12 route-feature"
            aria-labelledby="featured-route"
            data-testid="featured-route"
          >
            <div className="panel-head" style={{ marginBottom: 0 }}>
              <h2 id="featured-route">{featured.meta.name}</h2>
              <Link to={`/routen?route=${encodeURIComponent(featured.id)}`}>{t('home.routeDetails')}</Link>
            </div>
            <div className="spread" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="facts">
                <div>
                  <b>{fmtNumber(featured.meta.distanceM / 1000, 1)} km</b>
                  <small>{t('home.routeDistance')}</small>
                </div>
                <div>
                  <b>{fmtNumber(featured.meta.ascentM)} m</b>
                  <small>{t('home.routeAscent')}</small>
                </div>
                <div>
                  <b>{fmtNumber(Math.max(...featured.points.map((p) => p.y)))} m ü. M.</b>
                  <small>{t('home.routeTop')}</small>
                </div>
                {longest && (
                  <div>
                    <b>
                      {fmtNumber(longest.lengthM / 1000, 1)} km · Ø {fmtNumber(longest.avgGradePct, 1)} %
                    </b>
                    <small>{t('home.routeClimb')}</small>
                  </div>
                )}
              </div>
              <Link
                className="btn"
                to={`/fahren?route=${encodeURIComponent(featured.id)}`}
                data-testid="featured-ride"
              >
                {t('home.featuredCta')}
              </Link>
            </div>
            <ElevationProfile route={featured} showAhead={false} height={120} />
          </section>
        )}
      </div>
    </div>
  );
}
